'use client';

import { c as _c } from "react/compiler-runtime";
import { CodeIcon, CommandIcon, LinkIcon, LogInIcon, LogOutIcon, ShieldIcon, UserCircleIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { SOCIAL_LINKS } from '@/constants/navigation';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useSignInDialog } from '@/hooks/use-sign-in-dialog';
import { useSignOut } from '@/hooks/use-sign-out';
import { useRouter } from '@/i18n/navigation';
import { useSession } from '@/lib/auth-client';
type CommandAction = {
  title: string;
  icon: React.ReactNode;
  handleSelect: () => void | Promise<void>;
};
type CommandSection = {
  name: string;
  actions: CommandAction[];
};
export function CommandMenu() {
  const $ = _c(63);
  const [isOpen, setIsOpen] = useState(false);
  const [copy] = useCopyToClipboard();
  const {
    data: session
  } = useSession();
  const t = useTranslations();
  const {
    openDialog
  } = useSignInDialog();
  const router = useRouter();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      redirectTo: "/"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const signOut = useSignOut(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function closeMenu() {
      setIsOpen(false);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const closeMenu = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function openMenu() {
      setIsOpen(true);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openMenu = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function toggleMenu() {
      setIsOpen(_temp);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const toggleMenu = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function openExternalLink(url) {
      closeMenu();
      window.open(url, "_blank", "noopener");
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const openExternalLink = t4;
  let t5;
  if ($[5] !== copy) {
    t5 = async function copyCurrentUrl() {
      closeMenu();
      await copy({
        text: globalThis.location.href
      });
    };
    $[5] = copy;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const copyCurrentUrl = t5;
  let t6;
  if ($[7] !== router) {
    t6 = function handleAccountNavigate() {
      closeMenu();
      router.push("/account");
    };
    $[7] = router;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const handleAccountNavigate = t6;
  let t7;
  if ($[9] !== router) {
    t7 = function handleAdminNavigate() {
      closeMenu();
      router.push("/admin");
    };
    $[9] = router;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleAdminNavigate = t7;
  let t8;
  if ($[11] !== openDialog) {
    t8 = function handleSignIn() {
      closeMenu();
      openDialog();
    };
    $[11] = openDialog;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleSignIn = t8;
  let t9;
  if ($[13] !== signOut) {
    t9 = async function handleSignOut() {
      closeMenu();
      await signOut();
    };
    $[13] = signOut;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleSignOut = t9;
  let t10;
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const handleKeyDown = function handleKeyDown(event) {
        if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleMenu();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t11 = [];
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let T0;
  let T1;
  let T2;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[17] !== copyCurrentUrl || $[18] !== handleAccountNavigate || $[19] !== handleAdminNavigate || $[20] !== handleSignIn || $[21] !== handleSignOut || $[22] !== isOpen || $[23] !== session || $[24] !== t) {
    const accountActions = session ? [{
      title: t("common.labels.account"),
      icon: <UserCircleIcon />,
      handleSelect: handleAccountNavigate
    }, ...(session.user.role === "admin" ? [{
      title: "Admin",
      icon: <ShieldIcon />,
      handleSelect: handleAdminNavigate
    }] : []), {
      title: t("common.sign-out"),
      icon: <LogOutIcon />,
      handleSelect: handleSignOut
    }] : [{
      title: t("common.sign-in"),
      icon: <LogInIcon />,
      handleSelect: handleSignIn
    }];
    const generalActions = [{
      title: t("command-menu.actions.copy-link"),
      icon: <LinkIcon />,
      handleSelect: copyCurrentUrl
    }, {
      title: t("command-menu.actions.source-code"),
      icon: <CodeIcon />,
      handleSelect: () => {
        openExternalLink("https://github.com/nelsonlaidev/nelsonlai.dev");
      }
    }];
    const socialActions = SOCIAL_LINKS.map(link => ({
      title: link.title,
      icon: link.icon,
      handleSelect: () => {
        openExternalLink(link.href);
      }
    }));
    const groups = [{
      name: t("common.labels.account"),
      actions: accountActions
    }, {
      name: t("common.labels.general"),
      actions: generalActions
    }, {
      name: t("command-menu.groups.social"),
      actions: socialActions
    }];
    let t18;
    if ($[34] !== t) {
      t18 = t("command-menu.open-menu");
      $[34] = t;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let t19;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <CommandIcon />;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    if ($[37] !== t18) {
      t17 = <Button variant="ghost" size="icon" onClick={openMenu} aria-label={t18} data-testid="command-menu-button">{t19}</Button>;
      $[37] = t18;
      $[38] = t17;
    } else {
      t17 = $[38];
    }
    T2 = CommandDialog;
    t15 = isOpen;
    t16 = setIsOpen;
    T1 = Command;
    let t20;
    if ($[39] !== t) {
      t20 = t("command-menu.placeholder");
      $[39] = t;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    if ($[41] !== t20) {
      t14 = <CommandInput placeholder={t20} />;
      $[41] = t20;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    T0 = CommandList;
    let t21;
    if ($[43] !== t) {
      t21 = t("command-menu.no-results");
      $[43] = t;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    if ($[45] !== t21) {
      t12 = <CommandEmpty>{t21}</CommandEmpty>;
      $[45] = t21;
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    t13 = groups.map((group, index) => <Fragment key={group.name}><CommandGroup heading={group.name}>{group.actions.map(_temp2)}</CommandGroup>{index === groups.length - 1 ? null : <CommandSeparator />}</Fragment>);
    $[17] = copyCurrentUrl;
    $[18] = handleAccountNavigate;
    $[19] = handleAdminNavigate;
    $[20] = handleSignIn;
    $[21] = handleSignOut;
    $[22] = isOpen;
    $[23] = session;
    $[24] = t;
    $[25] = T0;
    $[26] = T1;
    $[27] = T2;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    T0 = $[25];
    T1 = $[26];
    T2 = $[27];
    t12 = $[28];
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
  }
  let t18;
  if ($[47] !== T0 || $[48] !== t12 || $[49] !== t13) {
    t18 = <T0>{t12}{t13}</T0>;
    $[47] = T0;
    $[48] = t12;
    $[49] = t13;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== T1 || $[52] !== t14 || $[53] !== t18) {
    t19 = <T1>{t14}{t18}</T1>;
    $[51] = T1;
    $[52] = t14;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== T2 || $[56] !== t15 || $[57] !== t16 || $[58] !== t19) {
    t20 = <T2 open={t15} onOpenChange={t16}>{t19}</T2>;
    $[55] = T2;
    $[56] = t15;
    $[57] = t16;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t17 || $[61] !== t20) {
    t21 = <>{t17}{t20}</>;
    $[60] = t17;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  return t21;
}
function _temp2(action) {
  return <CommandItem key={action.title} onSelect={action.handleSelect}>{action.icon}{action.title}</CommandItem>;
}
function _temp(value) {
  return !value;
}